import { ChangeDetectorRef, Component, DoCheck, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from "@angular/core";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  ReactionCountItem,
  ReactionMap,
  ReactionType,
  REACTION_EMOJI,
  REACTION_LABELS,
  REACTION_TYPES,
  applyReactionLocally,
  emptyReactionSet,
  getMyReactions,
  getReactionCounts,
  getUserIdsForReaction,
  normalizeReactions,
  UserService,
  avatarColorFor,
  initialsFor,
} from "@mypxplat/xplat/core";
import { AppService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { SimpleSignupComponent } from "../modals";
import { Subject, Subscription } from "rxjs";

export type ReactionChangeEvent = {
  comment: any;
  selectedType: ReactionType;
  previousTypes: ReactionType[];
  nextTypes: ReactionType[];
};

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-reaction-control",
  templateUrl: "reaction-control.component.html",
  styleUrls: ["reaction-control.component.scss"],
  standalone: false,
})
export class ReactionControlComponent implements OnInit, OnChanges, DoCheck, OnDestroy {
  private static touchPaletteOpened$ = new Subject<ReactionControlComponent>();

  @Input() comment: any;
  @Output() reactionChange = new EventEmitter<ReactionChangeEvent>();
  @Output() viewProfile = new EventEmitter<string>();

  public reactionTypes = REACTION_TYPES;
  public reactionEmoji = REACTION_EMOJI;
  public reactionLabels = REACTION_LABELS;

  public reactionsMap: ReactionMap = {};
  public myReactions: ReactionType[] = [];
  public myReactionSet: Record<ReactionType, boolean> = emptyReactionSet();
  public reactionCounts: ReactionCountItem[] = [];
  /**
   * The outline smiley on the add-a-reaction button. A field rather than an inline array
   * literal in the binding, so the reference is stable across change detection.
   *
   * `far` explicitly: only the REGULAR face-smile is registered, and angular-fontawesome
   * defaults to the `fas` prefix. Solid `face-smile` is a filled disc — it reads as a
   * reaction, not as a button that offers them.
   */
  public smileIcon: IconProp = ["far", "face-smile"];
  public reactionBarOpen: boolean = false;
  /** Palette hangs off the button's right edge instead of its left. See `alignBarWithinViewport`. */
  public barAlignRight: boolean = false;
  /** Same, for the reactors bubble list. See `alignReactorsWithinViewport`. */
  public reactorsAlignRight: boolean = false;
  public focusedReactionIndex: number = 0;

  public shouldShowReactorsList: boolean = false;
  public activeReactorType: ReactionType | null = null;
  public activeReactorUserIds: string[] = [];
  public activeHoveringReactorName: string = "";
  public activeHoveringReactorNameList: Record<string, string> = {};

  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private longPressTriggered: boolean = false;
  private touchInteraction: boolean = false;
  private paletteOpenedByTouch: boolean = false;
  private readonly longPressMs = 450;
  private lastReactionSignature: string = "";
  private closeBarTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly closeBarDelayMs = 200;
  private hideReactorsTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly hideReactorsDelayMs = 200;
  private outsideCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private touchPaletteSub: Subscription | null = null;
  private outsidePointerDown = (event: Event) => {
    if (!this.reactionBarOpen || !this.paletteOpenedByTouch) return;
    const host = this.elementRef.nativeElement as Node;
    const path = typeof (event as any).composedPath === "function" ? (event as any).composedPath() : [];
    if (path.includes(host) || (event.target && host.contains(event.target as Node))) return;
    this.closeReactionBar();
    this.changeDetector.markForCheck();
  };

  constructor(
    public userService: UserService,
    public communityService: WebCommunityService,
    public appService: AppService,
    private changeDetector: ChangeDetectorRef,
    private elementRef: ElementRef
  ) {}

  ngOnInit() {
    this.syncFromComment();
    this.touchPaletteSub = ReactionControlComponent.touchPaletteOpened$.subscribe((opened) => {
      if (opened !== this && this.reactionBarOpen) {
        this.closeReactionBar();
        this.changeDetector.markForCheck();
      }
    });
  }

  ngOnDestroy() {
    this.clearLongPressTimer();
    this.clearCloseBarTimer();
    this.clearHideReactorsTimer();
    this.unbindOutsideCloseListener();
    this.touchPaletteSub?.unsubscribe();
    this.touchPaletteSub = null;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["comment"]) {
      this.syncFromComment();
    }
  }

  ngDoCheck() {
    const signature = this.buildReactionSignature(this.comment);
    if (signature !== this.lastReactionSignature) {
      this.syncFromComment();
    }
  }

  syncFromComment() {
    this.reactionsMap = normalizeReactions(this.comment);
    this.myReactions = getMyReactions(this.reactionsMap, this.userService.user?.id);
    this.myReactionSet = emptyReactionSet();
    for (const type of this.myReactions) {
      this.myReactionSet[type] = true;
    }
    this.reactionCounts = getReactionCounts(this.reactionsMap);
    this.lastReactionSignature = this.buildReactionSignature(this.comment);
    if (this.comment) {
      this.comment.iLike = this.myReactionSet.like;
    }
  }

  private buildReactionSignature(comment: any): string {
    if (!comment) return "";
    return `${JSON.stringify(comment.reactions || {})}|${JSON.stringify(comment.likes || [])}`;
  }

  /**
   * Reactions require an account. For guests, open the sign-in / create-account modal instead of
   * reacting. Returns true when the user is a guest (caller should bail out); `onSuccess` re-runs
   * the intended reaction once they've signed up.
   */
  private promptGuestSignup(onSuccess?: () => void): boolean {
    if (this.userService.user) return false;
    this.appService.promptSimpleSignup(SimpleSignupComponent).then(
      () => onSuccess?.(),
      () => {}
    );
    return true;
  }

  /**
   * The button OPENS the palette; it never reacts on its own.
   *
   * It used to toggle a like, which made a control that looks like "add a reaction" quietly
   * pick one for you. Hover and click now do the same thing, so the two ways in agree — and a
   * pointer that can't hover (pen, touch) gets the same affordance.
   *
   * No guest prompt here either: opening a palette needs no account, and `selectReaction`
   * already prompts when they actually choose one.
   */
  onMainClick(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.longPressTriggered) {
      this.longPressTriggered = false;
      this.touchInteraction = false;
      return;
    }
    if (this.touchInteraction) {
      this.touchInteraction = false;
      // Only toggle-close if THIS control was already opened via touch.
      // Synthetic mouseenter must not count as "already open" on first tap.
      if (this.reactionBarOpen && this.paletteOpenedByTouch) {
        this.closeReactionBar();
        return;
      }
      this.openReactionBarFromTouch();
      return;
    }
    this.openReactionBar();
  }

  onMainKeydown(event: KeyboardEvent) {
    // Enter / Space open the palette rather than reacting, matching the pointer.
    if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      this.openReactionBar();
    }
  }

  onPointerDown(event: Event) {
    if ((event as PointerEvent).pointerType === "mouse") {
      this.touchInteraction = false;
      return;
    }
    this.touchInteraction = true;
    this.clearLongPressTimer();
    this.longPressTriggered = false;
    this.longPressTimer = setTimeout(() => {
      this.longPressTriggered = true;
      this.openReactionBarFromTouch();
      this.changeDetector.markForCheck();
    }, this.longPressMs);
  }

  onPointerUp() {
    this.clearLongPressTimer();
  }

  onPointerLeave() {
    this.clearLongPressTimer();
  }

  /** Desktop hover only — ignore synthetic mouseenter from touch. */
  onHoverOpenReactionBar() {
    if (this.touchInteraction || this.paletteOpenedByTouch) return;
    if (typeof window !== "undefined" && window.matchMedia && !window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      return;
    }
    this.openReactionBar();
  }

  openReactionBar(focusIndex?: number) {
    this.clearCloseBarTimer();
    // Reset before opening; `alignBarWithinViewport` re-decides from a real measurement.
    this.barAlignRight = false;
    this.reactionBarOpen = true;
    if (typeof focusIndex === "number") {
      this.focusedReactionIndex = focusIndex;
    } else if (this.myReactions.length) {
      const currentIndex = this.reactionTypes.indexOf(this.myReactions[0]);
      this.focusedReactionIndex = currentIndex >= 0 ? currentIndex : 0;
    } else {
      this.focusedReactionIndex = 0;
    }
    this.alignBarWithinViewport();
  }

  /**
   * Keep the palette on screen.
   *
   * It hangs off the button's LEFT edge and grows rightward, which is right in the detailed
   * view where the control starts the action row. In the community list view the control is
   * right-aligned in its row, and eleven reactions growing rightward from there run off the
   * screen — so flip it to hang off the right edge and grow back over the post.
   *
   * Measured, not estimated. A first attempt computed the width from the option count and
   * got it wrong by the inter-option gaps; rendering first and reading the real rect cannot
   * drift. `detectChanges()` puts the bar in the DOM synchronously and the correction runs
   * in the same task, so the browser only ever paints the final position — no visible flip.
   */
  private alignBarWithinViewport() {
    if (typeof window === "undefined") return;
    this.changeDetector.detectChanges();

    const host = this.elementRef?.nativeElement;
    const bar = host?.querySelector?.(".reaction-control__bar") as HTMLElement | null;
    // `left: 0` / `right: 0` resolve against `__main` — the add button — so that is what the
    // flip has to be measured against. NOT the host element: `<myp-reaction-control>` wraps
    // the button AND every count chip, so its box is the whole strip and is tens of pixels
    // wider. Measuring the host is what made the first attempt at this decide wrongly.
    const main = host?.querySelector?.(".reaction-control__main") as HTMLElement | null;
    if (!bar || !main) return;

    if (!this.overflowsRight(bar, main)) return;
    this.barAlignRight = true;
    this.changeDetector.detectChanges();
  }

  /**
   * Whether `popup`, laid out hanging off `anchor`'s LEFT edge, spills past the right edge of
   * the space it has — and whether hanging it off the right edge instead would actually fit.
   *
   * Both popups in this control (the reaction palette and the reactors bubble list) are
   * absolutely positioned at `left: 0` and grow rightward, so both run off screen when the
   * control sits at the right end of a row. This is the one place that decides.
   */
  private overflowsRight(popup: HTMLElement, anchor: HTMLElement): boolean {
    const MARGIN = 12;
    const rect = popup.getBoundingClientRect();
    const bounds = this.clippingBounds(this.elementRef.nativeElement);
    if (rect.right <= bounds.right - MARGIN) return false;
    // Only flip when it actually helps: where the popup is wider than the space available,
    // hanging it off the right edge just moves the overflow to the other side.
    return anchor.getBoundingClientRect().right - rect.width - MARGIN >= bounds.left;
  }

  /**
   * Same treatment for the reactors bubble list that opens off a count chip. Only one is ever
   * open at a time (`activeReactorType`), so a single flag covers every chip.
   */
  private alignReactorsWithinViewport() {
    if (typeof window === "undefined") return;
    this.reactorsAlignRight = false;
    this.changeDetector.detectChanges();

    const popup = this.elementRef?.nativeElement?.querySelector?.(".reaction-control__reactors") as HTMLElement | null;
    // The popup is absolutely positioned against its own count-chip `<li>`, which is its
    // parent — that is the anchor, not the control as a whole.
    const anchor = popup?.parentElement;
    if (!popup || !anchor) return;

    if (!this.overflowsRight(popup, anchor)) return;
    this.reactorsAlignRight = true;
    this.changeDetector.detectChanges();
  }

  /**
   * The edges the palette actually has to stay inside.
   *
   * NOT simply the viewport. The community feed scrolls inside its own container, and a
   * palette opened from a right-aligned control gets clipped by that container long before
   * it reaches the window edge — which is exactly what a viewport-only check missed. Walk up
   * to the first ancestor that clips (any non-visible overflow) and use its box, falling back
   * to the window when nothing clips.
   */
  private clippingBounds(host: HTMLElement): { left: number; right: number } {
    let el = host.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
      const style = window.getComputedStyle(el);
      if (style.overflowX !== "visible" || style.overflowY !== "visible") {
        const box = el.getBoundingClientRect();
        // Still bounded by the window — a clipping container can extend past it.
        return { left: Math.max(0, box.left), right: Math.min(window.innerWidth, box.right) };
      }
      el = el.parentElement;
    }
    return { left: 0, right: window.innerWidth };
  }

  private openReactionBarFromTouch(focusIndex?: number) {
    this.paletteOpenedByTouch = true;
    this.openReactionBar(focusIndex);
    ReactionControlComponent.touchPaletteOpened$.next(this);
    this.bindOutsideCloseListener();
  }

  scheduleCloseReactionBar() {
    // Touch opens stay open until a reaction is chosen, the control is toggled, or outside tap.
    if (this.paletteOpenedByTouch || this.touchInteraction) return;
    this.clearCloseBarTimer();
    this.closeBarTimer = setTimeout(() => {
      this.reactionBarOpen = false;
      this.closeBarTimer = null;
      this.changeDetector.markForCheck();
    }, this.closeBarDelayMs);
  }

  onControlMouseLeave() {
    this.scheduleCloseReactionBar();
  }

  closeReactionBar() {
    this.clearCloseBarTimer();
    this.unbindOutsideCloseListener();
    this.reactionBarOpen = false;
    this.paletteOpenedByTouch = false;
  }

  private bindOutsideCloseListener() {
    this.unbindOutsideCloseListener();
    this.outsideCloseTimer = setTimeout(() => {
      document.addEventListener("pointerdown", this.outsidePointerDown, true);
      this.outsideCloseTimer = null;
    }, 0);
  }

  private unbindOutsideCloseListener() {
    if (this.outsideCloseTimer) {
      clearTimeout(this.outsideCloseTimer);
      this.outsideCloseTimer = null;
    }
    document.removeEventListener("pointerdown", this.outsidePointerDown, true);
  }

  private clearCloseBarTimer() {
    if (this.closeBarTimer) {
      clearTimeout(this.closeBarTimer);
      this.closeBarTimer = null;
    }
  }

  onBarKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      this.closeReactionBar();
      return;
    }
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      this.focusedReactionIndex = (this.focusedReactionIndex + 1) % this.reactionTypes.length;
      return;
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      this.focusedReactionIndex = (this.focusedReactionIndex - 1 + this.reactionTypes.length) % this.reactionTypes.length;
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.selectReaction(this.reactionTypes[this.focusedReactionIndex]);
    }
  }

  selectReaction(selectedType: ReactionType) {
    // Covers reactions chosen from the palette, reaction counts, and keyboard activation.
    if (this.promptGuestSignup(() => this.selectReaction(selectedType))) return;
    const userId = this.userService.user?.id;
    if (!userId) return;
    const previousTypes = [...this.myReactions];
    this.reactionsMap = applyReactionLocally(this.reactionsMap, userId, selectedType);
    const nextTypes = getMyReactions(this.reactionsMap, userId);
    if (this.comment) {
      this.comment.reactions = { ...this.reactionsMap };
      this.comment.likeCount = Object.keys(this.reactionsMap).length;
      this.comment.iLike = nextTypes.includes("like");
    }
    this.syncFromComment();
    this.closeReactionBar();
    this.reactionChange.emit({
      comment: this.comment,
      selectedType,
      previousTypes,
      nextTypes,
    });
  }

  onCountClick(type: ReactionType, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectReaction(type);
  }

  public reactorPhotoStyles: Record<string, { "background-image": string }> = {};
  /**
   * Initials-fallback for reactors with no resolvable photo.
   *
   * The photo path below guesses a conventional S3 URL for any user we have no
   * cached photo for. That guess 404s for anyone whose avatar never lived there —
   * including every imported Discord author — leaving an empty bubble. These two
   * maps let the template render initials on a deterministic tint instead, matching
   * what comment.component does for post authors.
   */
  public reactorInitials: Record<string, string> = {};
  public reactorColors: Record<string, string> = {};
  public reactorHasPhoto: Record<string, boolean> = {};

  showReactors(type: ReactionType) {
    this.clearHideReactorsTimer();
    this.activeReactorType = type;
    this.activeReactorUserIds = getUserIdsForReaction(this.reactionsMap, type);
    this.shouldShowReactorsList = true;
    this.activeReactorUserIds.forEach((userId) => {
      const cached = this.communityService.cachedPhotos[userId];
      // Only trust a photo we actually have. The old unconditional S3 guess produced
      // a broken background-image for everyone else.
      this.reactorHasPhoto[userId] = !!cached;
      if (cached) {
        this.reactorPhotoStyles[userId] = { "background-image": `url(${cached})` };
      }
      this.reactorColors[userId] = avatarColorFor(userId);
      // Imported reactors have no resolvable profile — getUserProfile() cannot look up
      // an `imported:` ghost — so the importer stores their Discord display name on the
      // post as `reactor_names`. Without this fallback every imported bubble shows "?".
      const importedName = this.comment?.reactor_names?.[userId];
      const name = this.communityService.cachedNames[userId] || this.activeHoveringReactorNameList[userId] || importedName;
      if (importedName && !this.activeHoveringReactorNameList[userId]) {
        this.activeHoveringReactorNameList[userId] = importedName;
      }
      this.reactorInitials[userId] = initialsFor(name);
    });
    this.likeUserNames(this.activeReactorUserIds);
    this.alignReactorsWithinViewport();
  }

  scheduleHideReactors() {
    this.clearHideReactorsTimer();
    this.hideReactorsTimer = setTimeout(() => {
      this.hideReactorsTimer = null;
      this.shouldShowReactorsList = false;
      this.activeReactorType = null;
      this.activeReactorUserIds = [];
      this.activeHoveringReactorName = "";
      this.changeDetector.markForCheck();
    }, this.hideReactorsDelayMs);
  }

  private clearHideReactorsTimer() {
    if (this.hideReactorsTimer) {
      clearTimeout(this.hideReactorsTimer);
      this.hideReactorsTimer = null;
    }
  }

  reactorMouseEnter(userId: string) {
    this.clearHideReactorsTimer();
    if (this.communityService.cachedNames[userId]) {
      this.activeHoveringReactorName = this.communityService.cachedNames[userId];
      this.activeHoveringReactorNameList[userId] = this.communityService.cachedNames[userId];
    } else {
      this.communityService.getUserProfile(userId).then((result: any) => {
        // getUserProfile resolves without a profile document for deleted / imported / ghost
        // reactors; reading .firstName straight off that undefined result is the TypeError
        // reported to Bugsnag. Guard it and fall back the same way likeUserNames() does —
        // to any imported display name the post carries, then to nothing.
        const name = result ? `${result.firstName || ""} ${result.lastName || ""}`.trim() || result.name || "" : this.comment?.reactor_names?.[userId] || "";
        this.activeHoveringReactorName = name;
        this.activeHoveringReactorNameList[userId] = name;
        this.changeDetector.markForCheck();
      });
    }
  }

  reactorMouseLeave() {
    this.activeHoveringReactorName = "";
  }

  onViewProfile(userId: string) {
    this.viewProfile.emit(userId);
  }

  private likeUserNames(userIds: string[]) {
    userIds.forEach((userId) => {
      // Ghost ids have no profile document; the name came from the post itself above.
      if (String(userId).startsWith("imported:")) return;
      if (this.communityService.cachedNames[userId]) {
        this.activeHoveringReactorNameList[userId] = this.communityService.cachedNames[userId];
      } else {
        this.communityService.getUserProfile(userId).then((result: any) => {
          const name = `${result.firstName || ""} ${result.lastName || ""}`.trim() || result.name || "";
          this.activeHoveringReactorNameList[userId] = name;
          // Initials only become available once the profile resolves, so refresh them
          // here too — the synchronous pass above has nothing to work with yet.
          this.reactorInitials[userId] = initialsFor(name);
          if (result.photo) {
            this.communityService.cachedPhotos[userId] = result.photo;
            this.reactorPhotoStyles[userId] = { "background-image": `url(${result.photo})` };
            this.reactorHasPhoto[userId] = true;
          }
          this.changeDetector.markForCheck();
        });
      }
    });
  }

  private clearLongPressTimer() {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }
}
